import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, Coins, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { useToast } from "@/hooks/use-toast";
import SubscriptionPlans from "@/components/subscription/SubscriptionPlans";
import api from "@/lib/api";
import { SUBSCRIPTION } from "@/constants/testIds";

export default function SubscriptionTab() {
  const { t, language } = useI18n();
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(() => {
    api.get("/subscriptions/me").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  // "Real-time" quota: refreshed on mount, every 30s, and whenever the window regains focus -
  // the remaining count updates automatically right after each generation is paid.
  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    window.addEventListener("focus", load);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", load);
    };
  }, [load]);

  if (!status) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!status.active) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground" data-testid="subscription-no-sub">{t("subscription.noSubTitle")}</p>
        <SubscriptionPlans onSubscribed={load} />
      </div>
    );
  }

  const dateLocale = language === "fr" ? "fr-FR" : "en-US";
  const renewDate = status.period_end ? new Date(status.period_end).toLocaleDateString(dateLocale) : null;
  const pct = Math.min(100, Math.round((status.used / status.monthly_quota) * 100));
  const playManageUrl = `https://play.google.com/store/account/subscriptions?sku=${status.google_play_sku}&package=com.kalypsomusic.app`;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.post("/subscriptions/cancel");
      toast({ title: t("subscription.cancelledToast") });
      setConfirmCancel(false);
      load();
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: err?.response?.data?.detail });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card p-6" data-testid={SUBSCRIPTION.statusCard}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">{t("subscription.title")}</p>
            <p className="font-heading text-xl font-semibold">{status.plan_name}</p>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-center text-xs font-semibold ${
              status.cancel_at_period_end ? "bg-amber-500/10 text-amber-500" : "bg-success/10 text-success"
            }`}
            data-testid="subscription-status-badge"
          >
            {status.cancel_at_period_end ? t("subscription.cancelledBadge") : t("subscription.statusActive")}
          </span>
        </div>

        <div className="mt-6 text-center">
          <p className="font-heading text-5xl font-semibold text-primary" data-testid={SUBSCRIPTION.remaining}>
            {status.remaining}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{t("subscription.remainingThisMonth")}</p>
        </div>

        <div className="mt-5" data-testid={SUBSCRIPTION.usedBar}>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              {status.used} / {status.monthly_quota} {t("subscription.usedThisMonth")}
            </span>
            <span>{pct}%</span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-xl border border-border bg-background/50 p-3 text-sm" data-testid={SUBSCRIPTION.renewInfo}>
          <CalendarClock className="h-4 w-4 shrink-0 text-primary" />
          {status.cancel_at_period_end ? (
            <span>
              {t("subscription.activeUntil")} <strong>{renewDate}</strong>
            </span>
          ) : (
            <span>
              {t("subscription.renewsIn")} <strong>{status.days_until_renewal} {t("subscription.days")}</strong> ({renewDate})
            </span>
          )}
        </div>

        <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
          <Coins className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          {t("subscription.quotaHint")}
        </p>

        {status.provider === "google_play" ? (
          <a
            href={playManageUrl}
            target="_blank"
            rel="noreferrer"
            data-testid={SUBSCRIPTION.manageOnPlayLink}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ExternalLink className="h-4 w-4" />
            {t("subscription.manageOnPlay")}
          </a>
        ) : (
          !status.cancel_at_period_end &&
          (confirmCancel ? (
            <div className="mt-5 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
              <p className="text-sm font-medium">{t("subscription.cancelConfirmTitle")}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t("subscription.cancelKeepUntil")}</p>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  className="rounded-full"
                  data-testid={SUBSCRIPTION.cancelConfirmBtn}
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                  {t("subscription.cancelConfirmBtn")}
                </Button>
                <Button variant="outline" size="sm" className="rounded-full" data-testid="subscription-cancel-keep-btn" onClick={() => setConfirmCancel(false)}>
                  {t("subscription.cancelKeep")}
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              data-testid={SUBSCRIPTION.cancelBtn}
              onClick={() => setConfirmCancel(true)}
              className="mt-5 w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              {t("subscription.cancelBtn")}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
